import * as React from 'react';
import { Alert, Badge, ListGroup, Spinner, Stack } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  Event,
  EventRow,
  getEventRepeatGroupSummary,
  getEventRepeatOccurrenceBadges,
  EventRepeatOccurrenceBadge,
} from 'cb-model';
import { Section, useCacheQuery } from 'gossamer-front-end';
import { displayDateTimeToMinute, getFilter, getListRowLink } from 'gossamer-unified';

const BADGE_DETAILS: Record<EventRepeatOccurrenceBadge, { label: string; variant: string }> = {
  cancelled: { label: 'Cancelled', variant: 'danger' },
  edited: { label: 'Edited', variant: 'warning' },
  rescheduled: { label: 'Rescheduled', variant: 'info' },
  skipped: { label: 'Skipped', variant: 'secondary' },
  'pattern-exception': { label: 'Pattern exception', variant: 'secondary' },
  added: { label: 'Added', variant: 'success' },
};

interface RepeatedEventsSectionProps {
  currentEventId: number;
  repeatGroupId: string;
}

export const RepeatedEventsSection = ({
  currentEventId,
  repeatGroupId,
}: RepeatedEventsSectionProps): React.JSX.Element => {
  const events = useCacheQuery(Event, {
    where: { and: [getFilter('repeat_group_id', 'eq', repeatGroupId)] },
    order: [['start_at', 'ASC']],
  });
  const occurrences = events.data || [];
  const summary = getEventRepeatGroupSummary(occurrences);

  return (
    <Section title="Repeated Events" subtitle="Events created from this repeat pattern">
      {events.isFetching && !events.data && <Spinner animation="border" size="sm" />}
      {events.error && <Alert variant="danger">Unable to load repeated events.</Alert>}
      {!events.error && (
        <>
          <Stack direction="horizontal" gap={2} className="mb-3 flex-wrap">
            <Badge bg="primary">{summary.active} active</Badge>
            <Badge bg="danger">{summary.cancelled} cancelled</Badge>
            <Badge bg="warning" text="dark">
              {summary.exceptions} exceptions
            </Badge>
            <Badge bg="secondary">{summary.total} total</Badge>
          </Stack>
          {!events.isFetching && occurrences.length === 0 && <Alert variant="light">No repeated events found.</Alert>}
          {occurrences.length > 0 && (
            <ListGroup>
              {occurrences.map((occurrence) => (
                <RepeatedEventOccurrence
                  key={occurrence.id}
                  occurrence={occurrence}
                  isCurrent={occurrence.id === currentEventId}
                />
              ))}
            </ListGroup>
          )}
        </>
      )}
    </Section>
  );
};

const RepeatedEventOccurrence = ({
  occurrence,
  isCurrent,
}: {
  occurrence: EventRow;
  isCurrent: boolean;
}): React.JSX.Element => (
  <ListGroup.Item
    as="div"
    className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2"
    active={isCurrent}
  >
    <div>
      <Link
        className={isCurrent ? 'link-light fw-semibold' : 'fw-semibold'}
        to={getListRowLink(Event.listRowLink, String(occurrence.id))}
      >
        {occurrence.title}
      </Link>
      <div className={isCurrent ? 'small text-white-50' : 'small text-muted'}>
        {displayDateTimeToMinute(occurrence.start_at)}
      </div>
    </div>
    <Stack direction="horizontal" gap={1} className="flex-wrap">
      {isCurrent && (
        <Badge bg="light" text="dark">
          Current
        </Badge>
      )}
      {getEventRepeatOccurrenceBadges(occurrence).map((badge) => (
        <Badge key={badge} bg={BADGE_DETAILS[badge].variant}>
          {BADGE_DETAILS[badge].label}
        </Badge>
      ))}
    </Stack>
  </ListGroup.Item>
);
